import { ExternalLink } from "lucide-react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { ContactEchoesPanel } from "../echoflicks/EchoList";
import {
  channelLabel,
  contactDisplayName,
  hasActiveFunnelError,
  isRecoveredFunnelContact,
  stageLabel,
  stuckDays,
  type FunnelBoardContact,
} from "./funnelBoard";

function formatDate(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function ContactQuickViewModal({
  contact,
  onClose,
}: {
  contact: FunnelBoardContact | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={contact != null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-2xl">
        {contact ? <ModalBody contact={contact} onClose={onClose} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function ModalBody({
  contact,
  onClose,
}: {
  contact: FunnelBoardContact;
  onClose: () => void;
}) {
  const days = stuckDays(contact.stage_entered_at);
  const rating = contact.average_rating ?? null;
  const hasError = hasActiveFunnelError(contact);
  const recovered = isRecoveredFunnelContact(contact);

  return (
    <>
      <DialogHeader>
        <DialogTitle>{contactDisplayName(contact)}</DialogTitle>
        <DialogDescription asChild>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {contact.phone_e164 ? (
              <span className="tabular-nums">{contact.phone_e164}</span>
            ) : null}
            <Badge variant="outline" className="font-normal">
              {channelLabel(contact.acquisition_channel)}
            </Badge>
            <span>
              Stage: <strong>{stageLabel(contact.funnel_stage)}</strong>
              {days != null ? ` · stuck ${days}d` : ""}
            </span>
            {hasError ? (
              <Badge variant="destructive">Echo error</Badge>
            ) : recovered ? (
              <Badge
                variant="outline"
                className="border-emerald-500/40 text-emerald-700 dark:text-emerald-400"
              >
                Recovered after error
              </Badge>
            ) : null}
          </div>
        </DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metric label="Echoes" value={String(contact.video_count ?? 0)} />
        <Metric
          label="Completed"
          value={String(contact.completed_echo_count ?? 0)}
        />
        <Metric label="Avg rating" value={rating != null ? `${rating}` : "—"} />
        <Metric label="Arrived" value={formatDate(contact.first_seen)} />
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Echoes</h3>
        <ContactEchoesPanel contact={contact} />
      </div>

      <div className="flex justify-end">
        <Button asChild variant="outline" size="sm" onClick={onClose}>
          <Link to={`/contacts/${contact.id}/show`}>
            Open full profile
            <ExternalLink className="size-4" />
          </Link>
        </Button>
      </div>
    </>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
